import { RichTextHelper } from 'src/app/core/helpers/rich-text.helper';
import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ViewChild, ElementRef, Optional, Self,
} from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule, AbstractControl, NgControl, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormHelper } from 'src/app/core/helpers/form.helper';

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MatTooltipModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.css'],
})
export class RichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('editor', { static: true }) editorRef!: ElementRef<HTMLDivElement>;
  @ViewChild('linkInput') linkInputRef?: ElementRef<HTMLInputElement>;

  @Input() compact: boolean = false;
  @Input() placeholder: string = 'Digite o texto...';
  @Input() control: AbstractControl | null = null;
  @Input() showErrors: boolean = false;
  @Input() minChars: number = 0;
  @Input() inputId: string | null = null;
  @Input() label: unknown = null;

  @Output() valueChange = new EventEmitter<string>();

  protected showLinkPanel = false;
  protected disabled = false;
  protected linkError = '';
  protected linkUrl = '';
  protected currentCharCount = 0;
  protected insideLink = false;
  protected activeLinkHref = '';
  protected hoveredLinkHref = '';

  // Detecção de SO para tooltips
  readonly isMac: boolean = /Mac|iPhone|iPad/i.test(navigator.platform);

  get shortcutKey(): string { return this.isMac ? '⌘' : 'Ctrl'; }

  get tooltips() {
    const k = this.shortcutKey;
    return {
      bold:    `Negrito (${k}+B)`,
      italic:  `Itálico (${k}+I)`,
      underline: `Sublinhado (${k}+U)`,
      ulList:  'Lista com marcadores  (- espaço)',
      olList:  'Lista numerada  (1. espaço)',
      link:    `Inserir link (${k}+K)`,
      editLink: `Editar link (${k}+K)`,
      unlink:  `Remover link (${k}+${this.isMac ? 'Option' : 'Alt'}+K)`,
    };
  }

  private savedRange: Range | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private isComposing = false;
  private editorHasFocus = false;
  private cachedStates: Record<string, boolean> = {};

  constructor(@Optional() @Self() private ngControl: NgControl | null) {
    // Auto-registra como value accessor sem usar forwardRef no providers[],
    // evitando a colisão circular que impedia this.control de ser resolvido.
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (!this.control && this.ngControl?.control) {
      this.control = this.ngControl.control;
    }
  }

  ngOnDestroy(): void {}

  get hasError(): boolean {
    return !!this.control && this.control.invalid && this.control.touched;
  }

  get labelText(): string {
    return typeof this.label === 'string' ? this.label.trim() : '';
  }

  get required(): boolean {
    return (this.control ?? this.ngControl?.control)?.hasValidator(Validators.required) ?? false;
  }

  get errorMessages(): string[] {
    if (!this.control?.errors) return [];
    const msgs: string[] = [];
    if (this.control.errors['maxLengthRichText']) msgs.push('O texto excede o tamanho máximo permitido.');
    if (this.control.errors['required']) msgs.push('Este campo é obrigatório.');
    if (this.control.errors['minLengthRichText']) {
      msgs.push(`O texto deve conter no mínimo ${this.minChars || 10} caracteres válidos.`);
    }
    return msgs;
  }

  get counterLabel(): string {
    const n = this.currentCharCount;
    return `${n} ${n <= 1 ? 'caracter' : 'caracteres'}`;
  }

  get counterBelowMin(): boolean {
    return this.minChars > 0 && this.currentCharCount < this.minChars;
  }

  get displayedLinkHref(): string {
    return this.activeLinkHref || this.hoveredLinkHref;
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: string): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;
    if (editor.innerHTML !== (value ?? '')) {
      editor.innerHTML = RichTextHelper.sanitizeHtml(value ?? '');
      this.currentCharCount = RichTextHelper.count(value ?? '');
    }
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.closeLinkPanel();
    if (this.editorRef?.nativeElement) {
      this.editorRef.nativeElement.contentEditable = isDisabled ? 'false' : 'true';
    }
  }

  // ── Eventos do editor ─────────────────────────────────────────────────────

  onInput(): void {
    if (this.isComposing || this.disabled) return;
    const html = RichTextHelper.sanitizeHtml(this.editorRef.nativeElement.innerHTML);
    this.currentCharCount = RichTextHelper.count(html);
    this.onChange(html);
    this.valueChange.emit(html);
    const errors = { ...(this.control?.errors ?? {}) };
    delete errors['maxLengthRichText'];
    if (RichTextHelper.byteLength(html) > RichTextHelper.MAX_BYTES) errors['maxLengthRichText'] = true;
    this.control?.setErrors(Object.keys(errors).length ? errors : null);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled || this.isComposing) return;
    const selection = window.getSelection();
    if (!selection?.rangeCount || !this.editorRef.nativeElement.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
    const html = event.clipboardData?.getData('text/html');
    const plain = event.clipboardData?.getData('text/plain') ?? '';
    const sanitized = RichTextHelper.linkifyHtml(html ? RichTextHelper.sanitizeHtml(html, false, false) : RichTextHelper.plainTextHtml(plain));

    // Se o cursor estiver adjacente a uma lista, insere um bloco separador antes
    // do conteúdo colado para evitar que o browser mescle o resultado com a lista
    if (this.isAdjacentToAnyList()) {
      const sel = window.getSelection();
      const block = sel && sel.rangeCount > 0 ? this.getCurrentEditorBlock(sel.getRangeAt(0)) : null;
      if (block) {
        const sep = document.createElement('div');
        sep.appendChild(document.createElement('br'));
        block.parentNode!.insertBefore(sep, block);
        const sepRange = document.createRange();
        sepRange.setStart(sep, 0);
        sepRange.collapse(true);
        sel!.removeAllRanges();
        sel!.addRange(sepRange);
      }
    }

    document.execCommand('insertHTML', false, sanitized);
    this.onInput();
  }

  private isOpeningLinkPanel = false;

  onFocus(): void {
    if (this.disabled) return;
    document.execCommand('defaultParagraphSeparator', false, 'p');
    setTimeout(() => {
      this.editorHasFocus = true;
      // Restaura estado de formatação pendente perdido pelo browser ao perder o foco.
      // Só re-aplica se o cache diz ativo E o browser diz inativo (evita toggle acidental).
      for (const cmd of ['bold', 'italic', 'underline']) {
        if (this.cachedStates[cmd] && !document.queryCommandState(cmd)) {
          document.execCommand(cmd, false, undefined);
        }
      }
      this.syncFormattingState();
    });
  }

  onBlur(): void {
    this.editorHasFocus = false;
    if (this.isOpeningLinkPanel) return;
    this.onTouched();
  }
  onCompositionStart(): void { this.isComposing = true; }
  onCompositionEnd(): void { this.isComposing = false; this.onInput(); }

  onMouseup(): void {
    // Só sincroniza o estado de formatação se o editor JÁ ESTAVA focado antes deste clique.
    // No clique de re-foco, editorHasFocus ainda é false (setTimeout não rodou) — não sobrescreve o cache.
    if (this.editorHasFocus) this.syncFormattingState();
    this.updateLinkState();
  }

  onEditorMousemove(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement | null;
    this.hoveredLinkHref = (anchor && this.editorRef.nativeElement.contains(anchor))
      ? anchor.href
      : '';
  }

  onEditorMouseleave(): void {
    this.hoveredLinkHref = '';
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled || this.isComposing || event.isComposing) return;
    if (event.key === ' ' && this.tryAutoUrl()) {
      event.preventDefault();
      document.execCommand('insertText', false, ' ');
      this.onInput();
      return;
    }
    if (event.key === 'Enter') this.tryAutoUrl();
    // Atalhos manuais para garantir funcionamento mesmo sem foco nativo
    if (event.ctrlKey || event.metaKey) {
      const key = event.key.toLowerCase();
      if (!event.shiftKey && !event.altKey) {
        switch (key) {
          case 'b': event.preventDefault(); document.execCommand('bold', false); this.onInput(); return;
          case 'i': event.preventDefault(); document.execCommand('italic', false); this.onInput(); return;
          case 'u': event.preventDefault(); document.execCommand('underline', false); this.onInput(); return;
          case 'k': event.preventDefault(); this.openLinkPanel(event); return;
        }
      } else if (event.altKey && !event.shiftKey) {
        if (event.code === 'KeyK' || key === 'k') {
          event.preventDefault();
          if (this.insideLink) this.unlinkSelected();
          return;
        }
      }
    }
    if (event.key === 'Enter') this.handleEnter(event);
    if (event.key === 'Escape' && this.showLinkPanel) this.closeLinkPanel();
  }

  onKeyup(event: KeyboardEvent): void {
    if (this.disabled || this.isComposing || event.isComposing) return;
    if (event.key === ' ') {
      this.tryAutoList(event);
    }
    this.syncFormattingState();
    this.updateLinkState();
  }

  // ── Link panel ────────────────────────────────────────────────────────────

  openLinkPanel(event?: Event): void {
    if (this.disabled) return;
    this.linkError = '';
    if (event) event.preventDefault();
    this.savedRange = this.saveSelection();
    if (this.insideLink) {
      this.linkUrl = this.activeLinkHref;
      this.selectCurrentLink();
    } else {
      this.linkUrl = '';
    }
    this.showLinkPanel = !this.showLinkPanel;
    if (this.showLinkPanel) {
      this.isOpeningLinkPanel = true;
      setTimeout(() => {
        this.linkInputRef?.nativeElement?.focus();
        this.isOpeningLinkPanel = false;
      }, 60);
    }
  }

  confirmLink(): void {
    if (this.disabled) return;
    const url = RichTextHelper.normalizeUrl(this.linkUrl);
    if (!url) { this.linkError = 'Informe um endereço válido.'; return; }
    if (!this.savedRange || !this.editorRef.nativeElement.contains(this.savedRange.commonAncestorContainer)) return;
    this.restoreSelection(this.savedRange);
    document.execCommand('createLink', false, url);
    this.editorRef.nativeElement.querySelectorAll('a').forEach(a => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });
    this.onInput();
    this.closeLinkPanel();
    this.editorRef.nativeElement.focus();
    this.updateLinkState();
  }

  closeLinkPanel(): void {
    this.showLinkPanel = false;
    this.linkUrl = '';
    this.linkError = '';
    this.savedRange = null;
  }

  unlinkSelected(): void {
    if (this.disabled) return;
    this.editorRef.nativeElement.focus();
    this.selectCurrentLink();
    document.execCommand('unlink', false, undefined);
    this.insideLink = false;
    this.activeLinkHref = '';
    this.onInput();
  }

  openActiveLinkHref(): void {
    const href = this.displayedLinkHref;
    if (RichTextHelper.normalizeUrl(href, true)) window.open(href, '_blank', 'noopener,noreferrer');
  }

  // ── Toolbar ───────────────────────────────────────────────────────────────

  execCommand(command: string, value?: string): void {
    if (this.disabled) return;
    const wasUnfocused = !this.editorHasFocus;
    this.editorRef.nativeElement.focus();
    // Se o editor não estava focado, o browser perdeu o estado de formatação pendente.
    // Re-aplica antes do toggle para que o execCommand funcione como toggle correto.
    if (wasUnfocused && this.cachedStates[command] && !value) {
      document.execCommand(command, false, undefined);
    }
    document.execCommand(command, false, value);
    this.syncFormattingState(command);
    this.onInput();
  }

  insertUnorderedList(): void {
    if (this.disabled) return;
    if (this.isActive('insertUnorderedList')) { this.execCommand('insertUnorderedList'); return; }
    this.editorRef.nativeElement.focus();
    this.convertCurrentBlockToList('ul');
    this.onInput();
  }

  insertOrderedList(): void {
    if (this.disabled) return;
    if (this.isActive('insertOrderedList')) { this.execCommand('insertOrderedList'); return; }
    this.editorRef.nativeElement.focus();
    this.convertCurrentBlockToList('ol');
    this.onInput();
  }

  isActive(command: string): boolean {
    if (!this.editorHasFocus) return this.cachedStates[command] ?? false;
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return this.cachedStates[command] ?? false;
      if (!this.editorRef.nativeElement.contains(sel.getRangeAt(0).commonAncestorContainer)) return this.cachedStates[command] ?? false;
      return document.queryCommandState(command);
    } catch { return false; }
  }

  private syncFormattingState(onlyCommand?: string): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    if (!this.editorRef.nativeElement.contains(sel.getRangeAt(0).commonAncestorContainer)) return;
    const cmds = onlyCommand
      ? [onlyCommand]
      : ['bold', 'italic', 'underline', 'insertUnorderedList', 'insertOrderedList'];
    for (const cmd of cmds) {
      try { this.cachedStates[cmd] = document.queryCommandState(cmd); } catch {}
    }
  }

  // ── Privados ──────────────────────────────────────────────────────────────

  private updateLinkState(): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      this.insideLink = false;
      this.activeLinkHref = '';
      return;
    }

    const range = sel.getRangeAt(0);

    if (this.showLinkPanel && this.editorRef.nativeElement.contains(range.commonAncestorContainer)) {
      this.savedRange = range.cloneRange();
    }

    const anchor = this.getParentTag(range.commonAncestorContainer, 'A') as HTMLAnchorElement | null;
    this.insideLink = !!anchor;
    this.activeLinkHref = anchor?.href ?? '';
  }

  private selectCurrentLink(): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const anchor = this.getParentTag(sel.getRangeAt(0).startContainer, 'A');
    if (!anchor) return;
    const range = document.createRange();
    range.selectNode(anchor);
    sel.removeAllRanges();
    sel.addRange(range);
    this.savedRange = range.cloneRange();
  }

  private tryAutoUrl(): boolean {
    const sel = window.getSelection();
    const editor = this.editorRef.nativeElement;
    if (!sel || !sel.rangeCount || this.disabled || this.isComposing) return false;
    const range = sel.getRangeAt(0);
    if (!range.collapsed || !editor.contains(range.startContainer) || this.getParentTag(range.startContainer, 'A')) return false;
    const container = range.startContainer;
    if (container.nodeType !== Node.TEXT_NODE) return false;
    const before = (container.textContent ?? '').slice(0, range.startOffset);
    const link = RichTextHelper.findLinks(before).find(candidate => !before.slice(candidate.end).replace(/[.,;:!?\u201d\u2019"')\]}]/g, ''));
    if (!link) return false;
    const linkRange = document.createRange();
    linkRange.setStart(container, link.start);
    linkRange.setEnd(container, link.end);
    sel.removeAllRanges();
    sel.addRange(linkRange);
    if (!document.execCommand('createLink', false, link.href)) {
      sel.removeAllRanges(); sel.addRange(range); return false;
    }
    const anchor = this.getParentTag(sel.getRangeAt(0).startContainer, 'A') as HTMLAnchorElement | null;
    if (anchor) { anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; }
    const after = document.createRange();
    // A pontuação terminal permanece texto e o cursor volta ao fim da palavra original.
    if (anchor?.nextSibling?.nodeType === Node.TEXT_NODE) after.setStart(anchor.nextSibling, before.length - link.end);
    else if (anchor) after.setStartAfter(anchor);
    else { after.setStart(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset); }
    after.collapse(true);
    sel.removeAllRanges(); sel.addRange(after);
    this.onInput();
    return true;
  }

  private handleEnter(event: KeyboardEvent): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const li = this.getParentTag(sel.getRangeAt(0).startContainer, 'LI');
    if (!li || li.textContent?.trim()) return;

    event.preventDefault();
    const list = li.parentElement;
    if (!list) return;

    // Cria um div vazio real (não <br>) para o cursor não ficar adjacente ao <ol>/<ul>
    // Isso evita que o browser mescle listas ao chamar insertOrderedList novamente
    const div = document.createElement('div');
    div.appendChild(document.createElement('br'));
    list.parentElement?.insertBefore(div, list.nextSibling);
    list.removeChild(li);
    if (list.children.length === 0) list.remove();

    const newRange = document.createRange();
    newRange.setStart(div, 0);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    this.onInput();
  }

  /** Verifica se o cursor está em um bloco imediatamente após QUALQUER lista */
  private isAdjacentToAnyList(): boolean {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const block = this.getCurrentEditorBlock(sel.getRangeAt(0));
    if (!block || block.nodeType !== Node.ELEMENT_NODE) return false;
    const prevTag = block.previousElementSibling?.tagName;
    return prevTag === 'OL' || prevTag === 'UL';
  }

  /** Retorna o elemento bloco (div, p, etc) direto do editor que contém a seleção. Se for texto solto, envelopa num div. */
  private getCurrentEditorBlock(range: Range): Element | null {
    const editor = this.editorRef.nativeElement;

    // Cursor está diretamente no editor root (ex: editor vazio após delete) — cria bloco placeholder
    if (range.startContainer === editor) {
      const div = document.createElement('div');
      div.appendChild(document.createElement('br'));
      const ref = editor.childNodes[range.startOffset] ?? null;
      editor.insertBefore(div, ref);
      return div;
    }

    let node: Node | null = range.startContainer;
    while (node && node.parentNode !== editor) {
      node = node.parentNode;
    }
    if (!node || node === editor) return null;
    if (node.nodeType === Node.TEXT_NODE) {
      const div = document.createElement('div');
      editor.insertBefore(div, node);
      div.appendChild(node);
      return div;
    }
    return node as Element;
  }

  /** Converte o bloco atual numa lista sem chamar execCommand (evita o browser fazer auto-merge indesejado) */
  private convertCurrentBlockToList(tag: 'ol' | 'ul', style?: string, cssClass?: string): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const block = this.getCurrentEditorBlock(sel.getRangeAt(0));
    if (!block) return;

    // Previne merge automático do navegador inserindo um separador invisível
    if (block.previousElementSibling && ['OL', 'UL'].includes(block.previousElementSibling.tagName)) {
      const sep = document.createElement('div');
      sep.className = 'rte-list-separator';
      sep.innerHTML = '&#8203;'; // Zero-width space
      block.parentNode!.insertBefore(sep, block);
    }
    if (block.nextElementSibling && ['OL', 'UL'].includes(block.nextElementSibling.tagName)) {
      const sep = document.createElement('div');
      sep.className = 'rte-list-separator';
      sep.innerHTML = '&#8203;';
      block.parentNode!.insertBefore(sep, block.nextElementSibling);
    }

    const list = document.createElement(tag);
    if (style) list.style.listStyleType = style;
    if (cssClass) list.classList.add(cssClass);
    const li = document.createElement('li');

    // Move conteúdo restante (se houver, ex: texto digitado logo após o marcador)
    while (block.firstChild) {
      li.appendChild(block.firstChild);
    }
    if (!li.firstChild) li.appendChild(document.createElement('br'));

    list.appendChild(li);
    block.parentNode!.replaceChild(list, block);

    const newRange = document.createRange();
    newRange.setStart(li, 0);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }

  private tryAutoList(event: KeyboardEvent): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const container = range.startContainer;
    if (container.nodeType !== Node.TEXT_NODE) return;
    if (this.getParentTag(container, 'LI')) return;

    const text = container.textContent ?? '';
    const beforeCursor = text.slice(0, range.startOffset);

    let listCommand: 'insertUnorderedList' | 'insertOrderedList' | null = null;
    let listStyle: string | null = null;
    let listClass: string | null = null;
    let prefixLen = 0;

    // Comparações EXATAS — elimina qualquer ambiguidade entre tipos de marcador
    switch (beforeCursor) {
      case '- ': case '* ':   listCommand = 'insertUnorderedList'; prefixLen = 2; break;
      case '1. ':             listCommand = 'insertOrderedList'; listStyle = 'decimal'; prefixLen = 3; break;
      case 'a. ':             listCommand = 'insertOrderedList'; listStyle = 'lower-alpha'; prefixLen = 3; break;
      case 'A. ':             listCommand = 'insertOrderedList'; listStyle = 'upper-alpha'; prefixLen = 3; break;
      case 'i. ':             listCommand = 'insertOrderedList'; listStyle = 'lower-roman'; prefixLen = 3; break;
      case 'I. ':             listCommand = 'insertOrderedList'; listStyle = 'upper-roman'; prefixLen = 3; break;
      case 'a) ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-alpha-paren'; prefixLen = 3; break;
      case 'A) ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-upper-alpha-paren'; prefixLen = 3; break;
      case 'i) ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-lower-roman-paren'; prefixLen = 3; break;
      case 'I) ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-upper-roman-paren'; prefixLen = 3; break;
      case '1) ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-decimal-paren'; prefixLen = 3; break;
      case '1o ':
      case '1º ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-decimal-ordm'; prefixLen = 3; break;
      case '1a ':
      case '1ª ':             listCommand = 'insertOrderedList'; listClass = 'rte-list-decimal-ordf'; prefixLen = 3; break;
    }

    if (!listCommand) return;

    const prefixRange = document.createRange();
    prefixRange.setStart(container, 0);
    prefixRange.setEnd(container, prefixLen);
    sel.removeAllRanges();
    sel.addRange(prefixRange);
    document.execCommand('delete', false, undefined);

    this.convertCurrentBlockToList(listCommand === 'insertOrderedList' ? 'ol' : 'ul', listStyle ?? undefined, listClass ?? undefined);
    this.onInput();
  }

  private saveSelection(): Range | null {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 && this.editorRef.nativeElement.contains(sel.getRangeAt(0).commonAncestorContainer)
      ? sel.getRangeAt(0).cloneRange() : null;
  }

  private restoreSelection(range: Range | null): void {
    if (!range || !this.editorRef.nativeElement.contains(range.commonAncestorContainer)) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    try { sel.addRange(range); } catch { /* range obsoleto — ignora */ }
  }

  private getParentTag(node: Node, tagName: string): HTMLElement | null {
    if (!this.editorRef.nativeElement.contains(node)) return null;
    let current: Node | null = node;
    while (current && current !== this.editorRef.nativeElement) {
      if (current.nodeType === Node.ELEMENT_NODE && (current as HTMLElement).tagName === tagName) {
        return current as HTMLElement;
      }
      current = current.parentNode;
    }
    return null;
  }
}
